import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  Avatar,
  Tooltip,
  Chip,
  Card,
  CardContent,
  Tabs,
  Tab
} from '@mui/material';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import InfoIcon from '@mui/icons-material/Info';
import leaderboardServices from '../../services/leaderboard.services';
import UserModal from '../RecognitionModal/UserModal';
import { getDisplayNameFromEmail } from '../../utils/userUtils';
import styles from './leaderboard.module.css';

const WeeklyLeaderboard = () => {
  const [weeklyLeaderboard, setWeeklyLeaderboard] = useState([]);
  const [dailyLeaderboard, setDailyLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const fetchLeaderboards = async () => {
      setLoading(true);
      try {
        // Use the new combined endpoint to get both daily and weekly data in one call
        const combinedData = await leaderboardServices.getCombinedLeaderboard();

        // These now return arrays of top 20 users from the combined response
        setWeeklyLeaderboard(combinedData.weekly || []);
        setDailyLeaderboard(combinedData.daily || []);

        // Set date range from the first user's response data or from metadata
        if (combinedData.weekly?.length > 0 && combinedData.weekly[0].period) {
          const periodMatch = combinedData.weekly[0].period.match(/(\w+ \d+) - (\w+ \d+, \d+)/);
          if (periodMatch) {
            setDateRange({
              displayStart: periodMatch[1],
              displayEnd: periodMatch[2]
            });
          }
        }
      } catch (error) {
        console.error('Error fetching leaderboard data:', error);
        setError('Failed to load leaderboard data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboards();
  }, []);

  const getBadgeForRank = (rank) => {
    switch (rank) {
      case 0:
        return (
          <Tooltip title="1st Place">
            <Chip icon={<span style={{ fontSize: '16px' }}>🥇</span>} label="1st" color="primary" className={styles.badgeFirst} />
          </Tooltip>
        );
      case 1:
        return (
          <Tooltip title="2nd Place">
            <Chip icon={<WorkspacePremiumIcon className={styles.badgeIcon} />} label="2nd" className={styles.badgeSecond} />
          </Tooltip>
        );
      case 2:
        return (
          <Tooltip title="3rd Place">
            <Chip icon={<EmojiEventsIcon className={styles.badgeIcon} />} label="3rd" className={styles.badgeThird} />
          </Tooltip>
        );
      default:
        return null;
    }
  };

  const handleUserClick = (user, rank = null) => {
    setSelectedUser({ ...user, rank });
    setIsModalOpen(true);
  };

  const renderTopUserCards = (data, limit = 3) => (
    <div className={styles.cardGrid}>
      {data.slice(0, limit).map((user, index) => (
        <Card
          key={user.username}
          className={styles.achievementCard}
          elevation={2}
          onClick={() => handleUserClick(user, index + 1)}
          style={{ cursor: 'pointer' }}
        >
          <CardContent className={styles.cardContent}>
            <Box display="flex" flexDirection="column" alignItems="center" width="100%">
              <Avatar
                src={user.profilePictureUrl || ''}
                alt={user.displayName}
                className={styles.avatarLarge}
              >
                {!user.profilePictureUrl && (user.displayName || 'U').charAt(0).toUpperCase()}
              </Avatar>
              {getBadgeForRank(index)}
              <Typography variant="h6" className={styles.userName}>
                {user.preferredName || user.displayName || getDisplayNameFromEmail(user.email || user.username)}
              </Typography>
              
              <Box className={styles.userDetails}>
                <Box className={styles.detailItem}>
                  <PersonIcon fontSize="small" className={styles.detailIcon} />
                  <Typography variant="body2" color="text.secondary">
                    {user.username}
                  </Typography>
                </Box>
                {user.groups?.length > 0 && (
                  <Box className={styles.detailItem}>
                    <GroupIcon fontSize="small" className={styles.detailIcon} />
                    <Typography variant="body2" color="text.secondary">
                      {user.groups.join(', ')}
                    </Typography>
                  </Box>
                )}
                {user.city && (
                  <Box className={styles.detailItem}>
                    <LocationOnIcon fontSize="small" className={styles.detailIcon} />
                    <Typography variant="body2" color="text.secondary">
                      {user.city}
                    </Typography>
                  </Box>
                )}
              </Box>
              <div className={styles.activityCount}>
                {user.totalCount}
                <div className={styles.activityLabel}>Total Activity</div>
              </div>
            </Box>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  if (loading) {
    return (
      <Paper elevation={2} sx={{ p: 5, display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper elevation={2} sx={{ p: 5, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Paper>
    );
  }

  return (
    <div className={styles.leaderboardWrapper}>
      <div className={styles.leaderboardContainer}>
      {/* Employee of the Day Section */}
      <div className={styles.sectionHeader}>
        <div className={styles.sectionInfo}>
          <Typography variant="h4" className={styles.sectionTitle}>
            {dailyLeaderboard[0]?.isMonday ? 'Employee of the Day (Friday)' : 'Employee of the Day'}
          </Typography>
          <Tooltip title={dailyLeaderboard[0]?.isMonday ? "Friday's Top Performer (shown on Monday)" : "Yesterday's Top Performer"} arrow>
            <InfoIcon className={styles.infoIcon} />
          </Tooltip>
        </div>
        <Typography variant="subtitle1" className={styles.sectionSubtitle}>
          {dailyLeaderboard[0]?.period || 'No data available'}
        </Typography>
      </div>

      {renderTopUserCards(dailyLeaderboard)}

      {/* Employee of the Week Section */}
      <div className={styles.sectionHeader}>
        <div className={styles.sectionInfo}>
          <Typography variant="h4" className={styles.sectionTitle}>
            Employee of the Week
          </Typography>
          <Tooltip title="Work Week's Top Performer (Monday-Friday)" arrow>
            <InfoIcon className={styles.infoIcon} />
          </Tooltip>
        </div>
        <Typography variant="subtitle1" className={styles.sectionSubtitle}>
          {weeklyLeaderboard[0]?.period || `Top users for ${dateRange.displayStart} - ${dateRange.displayEnd}`}
        </Typography>
      </div>

      {/* Weekly top performers */}
      <div className={styles.cardGrid}>
        {weeklyLeaderboard.slice(0, 3).map((user, index) => (
          <Card 
            key={user.username} 
            className={styles.achievementCard}
            onClick={() => handleUserClick(user, index + 1)}
            style={{ cursor: 'pointer' }}
          >
            <CardContent className={styles.cardContent}>
              <Avatar
                src={user.profilePictureUrl || ''}
                alt={user.displayName}
                className={styles.avatarLarge}
              >
                {!user.profilePictureUrl && (user.displayName || 'U').charAt(0).toUpperCase()}
              </Avatar>
              {getBadgeForRank(index)}
              <Typography variant="h6" gutterBottom className={styles.userName}>
                {user.preferredName || user.displayName || getDisplayNameFromEmail(user.email || user.username)}
              </Typography>
              {/* User details */}
              <Box className={styles.userDetails}>
                <Box className={styles.detailItem}>
                  <PersonIcon fontSize="small" className={styles.detailIcon} />
                  <Typography variant="body2" color="text.secondary">
                    {user.username}
                  </Typography>
                </Box>
                {user.groups?.length > 0 && (
                  <Box className={styles.detailItem}>
                    <GroupIcon fontSize="small" className={styles.detailIcon} />
                    <Typography variant="body2" color="text.secondary">
                      {user.groups.join(', ')}
                    </Typography>
                  </Box>
                )}
                {user.city && (
                  <Box className={styles.detailItem}>
                    <LocationOnIcon fontSize="small" className={styles.detailIcon} />
                    <Typography variant="body2" color="text.secondary">
                      {user.city}
                    </Typography>
                  </Box>
                )}
              </Box>
              <div className={styles.activityCount}>
                {user.totalCount}
                <div className={styles.activityLabel}>Total Activity</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Table Section */}
      <Paper className={styles.tableSection}>
        <div className={styles.tableHeader}>
          <Typography className={styles.tableTitle}>
            Top 20 Users
          </Typography>
          <Tabs
            value={selectedTab}
            onChange={(e, val) => setSelectedTab(val)}
            className={styles.tabs}
          >
            <Tab
              label={dailyLeaderboard[0]?.isMonday ? "Employee of the Day (Friday)" : "Employee of the Day"}
              className={`${styles.tab} ${selectedTab === 0 ? styles.tabSelected : ''}`}
            />
            <Tab
              label="Employee of the Week (Work Week)"
              className={`${styles.tab} ${selectedTab === 1 ? styles.tabSelected : ''}`}
            />
          </Tabs>
        </div>

        <TableContainer sx={{ maxHeight: 500 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 60, fontWeight: 'bold' }}>Rank</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>User</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Group</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">
                  Total Activity
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(selectedTab === 0 ? dailyLeaderboard : weeklyLeaderboard).map((user, index) => (
                <TableRow
                  key={user.username}
                  onClick={() => handleUserClick(user, index + 1)}
                  sx={{
                    backgroundColor:
                      index < 3
                        ? `rgba(${index === 0 ? '30, 136, 229' : index === 1 ? '158, 158, 158' : '205, 127, 50'}, 0.08)`
                        : index % 2 === 0
                        ? 'rgba(0, 0, 0, 0.02)'
                        : 'inherit',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    },
                    cursor: 'pointer'
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body1" fontWeight="bold">
                        {index + 1}
                      </Typography>
                      {index < 3 && <Box sx={{ ml: 1 }}>{getBadgeForRank(index)}</Box>}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar
                        src={user.profilePictureUrl || ''}
                        alt={user.displayName}
                        sx={{
                          width: 40,
                          height: 40,
                          mr: 2,
                          bgcolor: !user.profilePictureUrl
                            ? index === 0
                              ? '#1E88E5'
                              : index === 1
                              ? '#9E9E9E'
                              : index === 2
                              ? '#CD7F32'
                              : '#188182'
                            : undefined
                        }}
                      >
                        {!user.profilePictureUrl && (user.displayName || 'U').charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body1">{user.preferredName || user.displayName || getDisplayNameFromEmail(user.email || user.username)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.username}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {user.groups?.length > 0 ? (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <GroupIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2">{user.groups.join(', ')}</Typography>
                      </Box>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body1" fontWeight="bold" color={index < 3 ? '#1d9f9f' : 'inherit'}>
                      {user.totalCount}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {(selectedTab === 0 ? dailyLeaderboard : weeklyLeaderboard).length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                    <Typography variant="body1">
                      No activity data for the {selectedTab === 0 ? 'selected day' : 'work week'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* User Modal */}
      <UserModal
        user={selectedUser}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        rank={selectedUser?.rank}
      />
      </div>
    </div>
  );
};

export default WeeklyLeaderboard;
